/* ============================================================
 * Servicio de Operaciones Pendientes (PendingCoreService)
 * ============================================================
 * Este servicio gestiona las operaciones locales que deben
 * sincronizarse con el backend (INSERT, UPDATE, DELETE).
 * Usa la tabla `pending_ops` en SQLite.
 * ============================================================ */

import { Injectable } from '@angular/core';
import { SqliteConexionService } from '../core-sqlite-services/sqlite-conexion.service';

/* ============================================================
 * Interfaz PendingOp: representa una operación pendiente.
 * ============================================================ */
export interface PendingOp {
  id?: number;
  table: string;
  action: 'INSERT' | 'UPDATE' | 'DELETE';
  payload: any;
  synced?: number;
  created_at?: string;
  deleted?: number;
  userId?: string;
  deviceToken?: string;
}

@Injectable({
  providedIn: 'root',
})
export class PendingCoreService {
  private table = 'pending_ops';

  constructor(private sqliteService: SqliteConexionService) {}

  /* ============================================================
   * addOp(op): agrega una nueva operación pendiente a la tabla.
   * ============================================================ */
  async addOp(op: PendingOp): Promise<void> {
    const sql = `
      INSERT INTO ${this.table} 
        ("table", "action", payload, synced, created_at, deleted, userId, deviceToken)
      VALUES (?, ?, ?, 0, datetime('now'), ?, ?, ?)
    `;
    await this.sqliteService.execute(sql, [
      op.table,
      op.action,
      JSON.stringify(op.payload),
      op.deleted ?? 0,
      op.userId ?? null,
      op.deviceToken ?? null
    ]);
  }

  async getPendingOps(): Promise<PendingOp[]> {
    const sql = `SELECT * FROM ${this.table} WHERE synced = 0 ORDER BY created_at ASC`;
    const res = await this.sqliteService.query(sql);
    return res.map(r => ({ ...r, payload: JSON.parse(r.payload) }));
  }

  async markAsSynced(id: number): Promise<void> {
    const sql = `UPDATE ${this.table} SET synced = 1 WHERE id = ?`;
    await this.sqliteService.execute(sql, [id]);
  }

  async clearAll(): Promise<void> {
    const sql = `DELETE FROM ${this.table}`;
    await this.sqliteService.execute(sql);
  }

  async markAsDeleted(id: number): Promise<void> {
    const sql = `UPDATE ${this.table} SET deleted = 1 WHERE id = ?`;
    await this.sqliteService.execute(sql, [id]);
  }
  

  async syncPendingOps(syncHandler: (op: PendingOp) => Promise<void>): Promise<void> {
    const ops = await this.getPendingOps();
    for (const op of ops) {
      try {
        await syncHandler(op);
        await this.markAsSynced(op.id!);
      } catch (err) {
        console.error('❌ Error sincronizando pending op:', op, err);
      }
    }
  }

  
}
